import { MicrosoftEvent } from '../interfaces/meta.interface';

/**
 * What the classifier knows about the applications that act in a tenant (KRAK-470).
 *
 * Absent, the classifier treats an application acting as itself exactly as it always did: a system event, graded
 * info before any rule runs. Present, Entra ID records such an actor performed reach the rules, which grade them by
 * privilege, by whether the application granted itself the role, and by whether the application is known.
 *
 * Every lookup is keyed BOTH by application id and by service-principal object id. The transform stores both on the
 * event, but the summary run reads events back through a DynamoDB index that projects `user_id` and not the actor
 * fields, so on that path the only identity available is the object id in `UserId` (`ServicePrincipal_<objectId>`).
 * A record whose `Actor` array carries no application-id GUID has the same shape. Keying by object id keeps
 * first-party recognition and naming working on both.
 */
export interface WorkloadIdentityContext {
    /**
     * Application ids of the Microsoft first-party applications present in the tenant (service principals whose
     * `appOwnerOrganizationId` is {@link MICROSOFT_FIRST_PARTY_TENANT_ID}), lowercased. {@link MICROSOFT_FIRST_PARTY_FLOOR}
     * is added for every tenant, so the floor holds where no inventory exists.
     */
    microsoftAppIds: Iterable<string>;
    /** Service-principal object ids of the same first-party applications, lowercased. */
    microsoftObjectIds: Iterable<string>;
    /** Application ids the MSP has allowed for the tenant, lowercased. Empty until Kraken's rule scope lands. */
    allowedAppIds: Iterable<string>;
    /** Display name by lowercased application id, from the Stolos service-principal inventory. */
    appDisplayNames: Record<string, string>;
    /** Display name by lowercased service-principal object id, from the same inventory. */
    displayNamesByObjectId: Record<string, string>;
}

/**
 * A context that knows nothing beyond the static floor: what a tenant with no Stolos inventory gets. Distinct from
 * passing no context at all, which keeps the pre-KRAK-470 behaviour.
 */
export const NO_WORKLOAD_IDENTITIES: WorkloadIdentityContext = {
    microsoftAppIds: [],
    microsoftObjectIds: [],
    allowedAppIds: [],
    appDisplayNames: {},
    displayNamesByObjectId: {},
};

/** The owner tenant of every Microsoft first-party application (`servicePrincipal.appOwnerOrganizationId`). */
export const MICROSOFT_FIRST_PARTY_TENANT_ID = 'f8cdef31-a31e-4b4a-93e4-5f571e91255a';

/**
 * Microsoft first-party applications that manage directory role membership in every tenant they serve, allow-listed
 * whether or not the tenant's inventory has been read. The GDAP principals are documented at
 * https://learn.microsoft.com/partner-center/customers/gdap-microsoft-led-transition; in the 30 days before
 * KRAK-469 the offline processor alone made 3,418 role assignments in 72 tenants. MS-PIM is not listed here because
 * Microsoft documents no application id for it; the tenant's inventory names it by owner tenant.
 */
export const MICROSOFT_FIRST_PARTY_FLOOR: readonly string[] = [
    '2832473f-ec63-45fb-976f-5d45a7d4bb91', // Partner customer delegated administration (GDAP)
    'a3475900-ccec-4a69-98f5-a65cd5dc5306', // Partner customer delegated admin offline processor (GDAP)
    'b39d63e7-7fa3-4b2b-94ea-ee256fdb8c2f', // Partner Center Delegated Admin Migrate (GDAP)
];

/** The `UserId` / `Actor` spelling of a service principal acting as itself: `ServicePrincipal_<objectId>`. */
const SERVICE_PRINCIPAL_ID = /^ServicePrincipal_(.+)$/i;

/**
 * The service-principal object id a `ServicePrincipal_<objectId>` identity names, lowercased, or null for any other
 * value. The one parser for that spelling: the transform, the predicates and the naming all read it here.
 *
 * @param value a `UserId`, an `Actor` or `Target` entry's `ID`, or anything else
 * @returns the object id, or null
 */
export function servicePrincipalObjectId(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }

    const match = SERVICE_PRINCIPAL_ID.exec(value);

    return match ? match[1].toLowerCase() : null;
}

/**
 * The acting service principal's object id, lowercased: the transform's `actor_object_id` when the event carries it,
 * else parsed from `user_id`, which is what the summary run's index projects.
 *
 * @param event the event
 * @returns the object id, or null when a person (or anything else) acted
 */
export function actorObjectId(event: MicrosoftEvent): string | null {
    return event.actor_object_id?.toLowerCase() ?? servicePrincipalObjectId(event.user_id);
}

/**
 * Whether an event was performed by a workload identity: an application acting as itself, app-only, in an Entra ID
 * record. Decided by the actor, never by `UserType` alone, which Microsoft stamps 4 (System) on these records and on
 * its own background processes alike.
 *
 * @param event the event
 * @returns true for an app-only Entra ID operation
 */
export function isWorkloadIdentityActor(event: MicrosoftEvent): boolean {
    return event.workload === 'AzureActiveDirectory' && actorObjectId(event) !== null;
}

/**
 * The acting application's display name: the one the record carried (`description_fields.actorApp`, set by the
 * transform from the `Actor` array), else the inventory's, by application id then by object id. One derivation for
 * the alert text and the `actor_name` column, so the two never name different applications for one row.
 *
 * @param event the event
 * @param context the tenant's application inventory, or undefined
 * @returns the name, or null when neither source has one
 */
export function actorDisplayNameFrom(
    event: MicrosoftEvent,
    context: Pick<WorkloadIdentityContext, 'appDisplayNames' | 'displayNamesByObjectId'> | undefined,
): string | null {
    const fields = event.description_fields as { actorApp?: unknown } | null | undefined;

    if (typeof fields?.actorApp === 'string' && fields.actorApp.length > 0) {
        return fields.actorApp;
    }

    if (!context) {
        return null;
    }

    const appId = event.actor_app_id?.toLowerCase();
    const objectId = actorObjectId(event);

    return (appId && context.appDisplayNames[appId]) || (objectId && context.displayNamesByObjectId[objectId]) || null;
}

/**
 * The event with `description_fields.actorApp` filled from the inventory when the record did not name the acting
 * application itself, so a description can say "ConnectWise Cloud Management - Exchange Online" rather than a GUID.
 *
 * @param event the event
 * @param context the tenant's application inventory, or undefined
 * @returns the same event, or a copy carrying the name
 */
export function withActorApp(
    event: MicrosoftEvent,
    context: Pick<WorkloadIdentityContext, 'appDisplayNames' | 'displayNamesByObjectId'> | undefined,
): MicrosoftEvent {
    const fields = event.description_fields as Record<string, unknown> | null | undefined;

    if (fields && typeof fields['actorApp'] === 'string') {
        return event;
    }

    const name = actorDisplayNameFrom(event, context);

    return name ? { ...event, description_fields: { ...(fields ?? {}), actorApp: name } } : event;
}
